/**
 * One glossary entry.
 *
 * The shortest page shape on the site, and deliberately so: H1 is the term,
 * one grey line, then the definition. A definition page that opens with three
 * paragraphs of preamble is a definition page nobody reads and no assistant
 * quotes — the answer has to be the first prose on the page.
 *
 * The link to the measuring tool sits directly under the definition rather than
 * at the bottom, because "what is canthal tilt" and "what is MY canthal tilt"
 * are the same visit about eighty percent of the time. Where there is no tool,
 * the page says so plainly instead of linking somewhere adjacent — eleven of
 * these terms are in that position and pretending otherwise would send
 * a reader to a page about something else.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";
import { GLOSSARY_DIAGRAM } from "../../lib/glossary/diagrams";
import { glossaryIndexPath, glossaryPath } from "../../lib/glossary/index";
import type {
  GlossaryBundle,
  GlossaryEntry,
  GlossarySlug,
} from "../../lib/glossary/types";
import type { GlossaryUi } from "../../lib/glossary/ui";
import { toolPath } from "../../lib/tools/index";
import type { ToolCopy, ToolSlug } from "../../lib/tools/types";
import { ContentFooter } from "../ContentFooter";
import { FaceDiagram } from "../FaceDiagram";

export function GlossaryPage({
  slug,
  entry,
  ui,
  lang,
  bundle,
  toolCopy,
}: {
  slug: GlossarySlug;
  entry: GlossaryEntry;
  ui: GlossaryUi;
  lang: ContentLang;
  bundle: GlossaryBundle;
  /** Only the linked tool's copy, for its H1. Null when there is no tool. */
  toolCopy: Record<ToolSlug, ToolCopy> | null;
}) {
  const diagram = GLOSSARY_DIAGRAM[slug];

  return (
    <article className="mx-auto w-full max-w-2xl px-6 py-12 sm:py-16">
      <Link
        to={glossaryIndexPath(lang)}
        className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase underline decoration-hair underline-offset-4"
      >
        {ui.backToGlossary}
      </Link>

      <h1 className="mt-5 font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {entry.term}
      </h1>
      <p className="mt-3 text-lg leading-relaxed text-ink2">
        {entry.standfirst}
      </p>

      {/* The definition and its picture, side by side on a wide screen and
          stacked on a phone. The diagram sits WITH the answer rather than after
          it: a reader who cannot picture a gonial angle needs the drawing while
          reading the sentence, not once they have given up on it. Terms with no
          honest front-facing geometry have no diagram — `diagrams.ts` records
          which and why. */}
      <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-start">
        <p className="flex-1 text-[17px] leading-[1.7] text-body">
          {entry.answer}
        </p>
        {diagram ? (
          <FaceDiagram
            metric={diagram}
            label={ui.diagramLabel.replace("%s", entry.term)}
            className="h-auto w-full max-w-[200px] shrink-0 self-center sm:self-start"
          />
        ) : null}
      </div>

      {entry.tool && toolCopy ? (
        <div className="brutal mt-9 p-6">
          <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
            {ui.measureIt}
          </p>
          <Link
            to={toolPath(entry.tool, lang)}
            className="mt-2 block font-display text-xl font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
          >
            {toolCopy[entry.tool].h1}
          </Link>
          <p className="mt-2 text-[15px] leading-relaxed text-ink2">
            {toolCopy[entry.tool].measures}
          </p>
        </div>
      ) : (
        <p className="mt-9 border-l-[3px] border-hair pl-4 text-[15px] leading-relaxed text-ink2">
          {ui.noTool}
        </p>
      )}

      <section className="mt-10 border-t-[2.5px] border-ink pt-7">
        <h2 className="font-display text-xl font-bold tracking-tight text-ink">
          {ui.seeAlso}
        </h2>
        <ul className="mt-3 flex flex-col gap-2">
          {entry.seeAlso.map((slug: GlossarySlug) => (
            <li key={slug}>
              <Link
                to={glossaryPath(slug, lang)}
                className="font-display font-bold text-brand underline decoration-hair underline-offset-4 hover:decoration-brand"
              >
                {bundle[slug].term}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <ContentFooter lang={lang} />
    </article>
  );
}
